import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { getAllSports, getAllFacilities } from '../../services/api';

// Async thunks for fetching filter data
export const fetchSportsForFilters = createAsyncThunk(
  'pitchFilters/fetchSports',
  async (_, { rejectWithValue }) => {
    try {
      const response = await getAllSports();
      // Check if response has the correct structure
      if (response.data && response.data.sports) {
        return response.data.sports; // Assuming the API returns { sports: [...] }
      }
      return rejectWithValue('Invalid sports data format');
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchFacilitiesForFilters = createAsyncThunk(
  'pitchFilters/fetchFacilities',
  async (_, { rejectWithValue }) => {
    try {
      const response = await getAllFacilities();
      if (response.data.status && response.data.facilities) {
        // Map the facilities and keep the populated availableSport data as is
        return response.data.facilities.map(facility => ({
          _id: facility._id,
          name: facility.name,
          startingFrom: facility.startingFrom, // Include starting price for validation
          availableSports: facility.availableSport, // This is already populated from backend
          venueTimings: facility.venueTimings,
          isActive: facility.isActive,
          contractTenure: facility.contractTenure
        }));
      }
      return rejectWithValue('No facilities data found');
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

const initialState = {
  sports: [],
  facilities: [],
  loading: false,
  error: null,
  selectedFacility: null,
  selectedSport: null
};

const pitchFiltersSlice = createSlice({
  name: 'pitchFilters',
  initialState,
  reducers: {
    setSelectedFacility: (state, action) => {
      state.selectedFacility = action.payload;
      // Reset selected sport when facility changes
      state.selectedSport = null;
    },
    setSelectedSport: (state, action) => {
      state.selectedSport = action.payload;
    },
    clearFilters: (state) => {
      state.selectedSport = null;
      state.selectedFacility = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // Sports
      .addCase(fetchSportsForFilters.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSportsForFilters.fulfilled, (state, action) => {
        state.loading = false;
        state.sports = action.payload;
      })
      .addCase(fetchSportsForFilters.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Facilities
      .addCase(fetchFacilitiesForFilters.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchFacilitiesForFilters.fulfilled, (state, action) => {
        state.loading = false;
        state.facilities = action.payload;
      })
      .addCase(fetchFacilitiesForFilters.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  }
});

export const { setSelectedFacility, setSelectedSport, clearFilters } = pitchFiltersSlice.actions;

// Selectors
export const selectFacilities = state => state.pitchFilters.facilities;
export const selectSports = state => state.pitchFilters.sports;
export const selectSelectedFacility = state => state.pitchFilters.selectedFacility;
export const selectSelectedSport = state => state.pitchFilters.selectedSport;
export const selectLoading = state => state.pitchFilters.loading;
export const selectError = state => state.pitchFilters.error;

export default pitchFiltersSlice.reducer; 